import { outsource } from "@/api/outsource/useOutsourceQuery";
import { customFunctions } from "@/config/customFunction";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import customAlert from "@/features/ui/alert/alert";
import useWorkMutation from "@/features/workByManagement/api/useWorkMutation";
import { workType } from "@/features/workByManagement/context/WorkProvider";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useWorkState } from "@/features/workByManagement/hooks/useWorkState";
import { Button, Flex, Text, TextInput } from "@mantine/core";
import {
  AuthSignupPost201ResponseEquipmentWorksInner,
  DefaultApiWmsOutsourceInventoryLogPostRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { MantineReactTable, MRT_ColumnDef } from "mantine-react-table";
import { useEffect, useMemo, useState } from "react";

const AdjustmentAndInput = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Wrapper: FormWrapper,
  Table: MantineReactTable,
  Text: Text,
  Flex: Flex,
});

export const AdjustmentAndInputForm = () => {
  const state = useWorkState();
  const { closeModal } = useModal();
  const actions = useWorkActions();
  const queryClient = useQueryClient();

  const { updateQuantityMutate, updateDefectMutate } = useWorkMutation();
  const { workEndMutation } = useWorkMutation();
  const [quantity, setQuantity] = useState("");

  const { mutate: fetchLogMutate } = useMutation(
    (params: DefaultApiWmsOutsourceInventoryLogPostRequest) =>
      outsource.inventoryLog(params).mutationFn(undefined),
    {
      onSuccess: (data) => {
        setQuantity(data.data.quantity ?? "0");
      },
      onError: (error: any) => {
        customAlert(
          error.response?.data?.message ?? t("workMessage.fetchLogFail"),
          t("workMessage.fetchLogFailTitle"),
          "red",
        );
        closeModal({});
      },
    },
  );

  useEffect(() => {
    fetchLogMutate({
      wmsOutsourceInventoryLogPostRequest: {
        workId: state.works[0]?.id,
      },
    });
  }, []);

  const calculateConsumedMaterial = (selectedRow: workType): number => {
    const baseQuantity = getBomQuantity(selectedRow);
    const performance = getPerformanceQuantity(selectedRow);
    return performance * baseQuantity;
  };

  const getBomQuantity = (selectedRow: workType): number => {
    return Number(selectedRow?.routingData?.routingBoms?.[0]?.quantity ?? 0);
  };

  const getPerformanceQuantity = (selectedRow: workType): number => {
    const tempValue = state.tempQuantity.find((data) => data.key === String(selectedRow.id))?.value;

    if (tempValue && tempValue !== "0") {
      return Number(tempValue);
    }

    const endValue = selectedRow.summary?.end;
    if (endValue === "0") {
      return Number(selectedRow.targetQuantity ?? 0);
    }

    return Number(endValue ?? 0);
  };

  // const isButtonDisabled = Number(quantity ?? 0) < calculateConsumedMaterial(state.works[0]);

  const convertedData = [
    {
      remainRawMaterial: quantity || "-",
      lotNumber: "-",
      expectedConsumedRawMaterial: calculateConsumedMaterial(state.works[0]).toFixed(3),
    },
  ];

  const handleUpdateQuantity = async (work: workType): Promise<boolean> => {
    const quantity = getPerformanceQuantity(work);

    if (work.id && quantity) {
      try {
        await updateQuantityMutate(
          {
            workId: work.id,
            worksWorkIdPerformancePutRequest: {
              accQuantity: String(quantity),
              warnInsufficiency: customFunctions.ADD_PRODUCTION_RECORD_SAVE_BOM_INVENTORY_ERROR,
              priorityConsume: true, // 투입한거 우선소모 boolean
            },
          },
          {
            onSuccess: () => {
              if (!state.tempDefects.length) {
                queryClient.invalidateQueries(["inventories"]);
                queryClient.invalidateQueries(["workPage"]);
                customAlert("실적 등록에 성공하였습니다.", "실적 등록 성공", "green");
                closeModal({});
              }
            },
          },
        );
        return true;
      } catch (error: any) {
        customAlert(
          error.response?.data?.message ?? t("workMessage.outOfStock"),
          t("workMessage.outOfStockFail"),
          "red",
        );
        return false;
      }
    }
    return false;
  };

  const handleUpdateDefects = async (
    work:
      | ProductionPlansGet200ResponseRowsInnerWorksInner
      | AuthSignupPost201ResponseEquipmentWorksInner,
  ): Promise<boolean> => {
    const defectData = state.tempDefects.find(
      (data) => data.id === (work.id && work.id.toString()),
    );
    // Update one defect at a time
    const updateDefectSequentially = async (defect: { key: string; value: string }) => {
      try {
        if (work.id) {
          await updateDefectMutate({
            workId: work.id,
            worksWorkIdDefectPutRequest: {
              defectCode: defect.key,
              accQuantity: defect.value,
            },
          });
        }
      } catch (e: any) {
        console.error("Error updating defect: ", e);
        throw e; // 에러를 다시 throw하여 밖에서 처리할 수 있도록 합니다.
      }
    };

    if (work.id && defectData) {
      for await (const defect of defectData.defect) {
        try {
          // Await each update operation before continuing to the next one
          await updateDefectSequentially(defect);
        } catch (e: any) {
          if (e.response?.data?.message === "defectQuantity is greater than startQuantity") {
            alert(t("workMessage.defectSaveFail"));
            return false; // 실패 시 종료
          }
          // Handle other unexpected errors
          alert(t("workMessage.error"));
          return false; // 실패 시 종료
        }
      }
      // Reset temporary defects and set update success state
      actions.setTempDefects([]);
      queryClient.invalidateQueries(["workPage"]);
      return true;
    }
    // 결함 데이터가 없으면 결함 업데이트는 성공으로 간주
    return true;
  };

  const handleProductionInput = async () => {
    const quantityOk = await handleUpdateQuantity(state.works[0]);
    if (!quantityOk) return; // 실적 저장 실패 시 작업종료하지 않음
    const defectOk = await handleUpdateDefects(state.works[0]);

    // 생산실적 등록 성공 시 작업종료 처리
    const workId = state.works?.[0]?.id ?? 0;
    // 외주 작업지시에서만 사용되므로 바로 작업종료 처리
    if (workId && defectOk) {
      workEndMutation(
        {
          workId,
          // 기존 사용 패턴과 동일한 요청 필드 사용
          worksWorkIdTrackingStartPostRequest: {
            workId,
          },
        },
        {
          onSuccess: () => {
            customAlert(t("workMessage.workEnd"), t("workStatus.timeTrackingEnd"), "info");
            queryClient.invalidateQueries(["workPage"]);
            actions.setWorks((prevWorks) => []);
            actions.setScrapWorkIds([]);
          },
          onError: () => {
            customAlert(t("workMessage.workEndFail"), t("workStatus.timeTrackingEnd"), "red");
          },
        },
      );
    }
  };

  const columns = useMemo<MRT_ColumnDef[]>(
    () => [
      {
        accessorKey: "remainRawMaterial",
        header: "잔여 원자재량",
      },
      {
        accessorKey: "lotNumber",
        header: "로트번호",
      },
      {
        accessorKey: "expectedConsumedRawMaterial",
        header: "예상 소모 원자재량",
      },
    ],
    [],
  );

  return (
    <AdjustmentAndInput.Wrapper miw={"50rem"}>
      <AdjustmentAndInput.Table
        enableRowSelection={false}
        enableRowNumbers={false}
        enableColumnActions={false}
        enableColumnOrdering={false}
        enableSorting={false}
        enableTopToolbar={false}
        enablePagination={false}
        enableBottomToolbar={false}
        enableRowActions={false}
        columns={columns}
        data={[{ ...convertedData[0], remainRawMaterial: quantity }]}
      />
      <AdjustmentAndInput.Text>
        {"* 생산실적 등록 : 원자재량 변경 없이 선택된 작업지시의 생산수량 등록"}
      </AdjustmentAndInput.Text>

      <Flex justify={"space-between"}>
        <AdjustmentAndInput.Button onClick={() => closeModal({})} variant="outline" color="gray">
          {"취소"}
        </AdjustmentAndInput.Button>
        <Flex gap={"md"}>
          <AdjustmentAndInput.Button
            // disabled={isButtonDisabled}
            color="cyan"
            onClick={handleProductionInput}
          >
            {"생산실적 등록"}
          </AdjustmentAndInput.Button>
        </Flex>
      </Flex>
    </AdjustmentAndInput.Wrapper>
  );
};
